import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, useCharger } from "./charger";
import { useMoi } from "./moi";

/* Clé API personnelle (19/10/2026) : pour piloter le CRM depuis Claude Code. Elle ne donne
   accès qu'au CRM et à la boîte mail de son titulaire, jamais au tableau de bord. */

const BASE = typeof window !== "undefined" ? window.location.origin : "https://crm.atelieraugmente.fr";

function consigne(cle: string, boite: string) {
  return `# CRM Atelier Augmenté : accès pour Claude Code

Tu as accès au CRM d'Atelier Augmenté (prospects, sociétés, affaires, notes, tâches) et à ma boîte mail professionnelle (${boite || "mon adresse @atelieraugmente.fr"}). Rien d'autre : pas de tableau de bord, pas de facturation.

Adresse : ${BASE}
Authentification : en-tête « Authorization: Bearer ${cle} » sur chaque requête.
Garde cette clé pour toi : ne l'écris jamais dans un fichier partagé ni dans un dépôt git.

## Le CRM (JSON)
- GET  /api/v2/moi : qui je suis.
- GET  /api/v2/contacts?q=texte&limit=50&offset=0 : liste et recherche (même chose pour companies, deals, contact_notes, deal_notes, tasks, tags). Filtre exact possible : ?company_id=12, ?status=hot.
- GET  /api/v2/contacts/123 : une fiche.
- POST /api/v2/contacts {first_name, last_name, title, company_id, email_jsonb:[{email, type:"Work"}], phone_jsonb:[{number, type:"Work"}], background} : ajouter un lead.
- POST /api/v2/companies {name, sector, website, city, description}
- POST /api/v2/contact_notes {contact_id, text, status: cold|warm|hot|in-contract}
- POST /api/v2/tasks {contact_id, type: call|email|follow-up|meeting, text, due_date}
- POST /api/v2/deals {name, company_id, contact_ids:[…], stage: opportunity|appel-fait|proposal-sent|in-negociation|won|lost, amount, category}
- PATCH /api/v2/<ressource>/<id> {champs à changer}, DELETE /api/v2/<ressource>/<id>
- GET  /api/simulateur/contact/<id du contact> : son résultat au simulateur et le lien du simulateur à son nom.
- POST /api/simulateur/contact/<id du contact>/appel {resultat: joint|interesse|rappeler|messagerie|pas-de-reponse|pas-interesse, note} : noter un appel passé (compté dans mes chiffres).
- GET  /api/simulateur/mes-chiffres : mes appels, mails envoyés, dossiers closés et payés, ma commission.

## La boîte mail
- GET  /api/crm/boite?dossier=recus|envoyes|brouillons|archives&q=texte : la liste.
- GET  /api/crm/boite/<id> : un mail complet (et les réponses déjà envoyées).
- GET  /api/crm/boite/modeles : les modèles ; POST /api/crm/boite/modeles/<id>/remplir {prospectId} : un modèle rempli pour un prospect.
- POST /api/crm/boite/brouillons {a, cc, sujet, texte, prospectId, filDe} : enregistrer un brouillon (ou {id, …} pour le modifier).
- POST /api/crm/boite/envoyer {a, cc, sujet, texte, filDe, brouillonId, pieces:[{nom, type, base64}]} : envoyer (filDe = id du mail auquel on répond). La signature est ajoutée automatiquement.

## Règles à respecter
1. Jamais d'envoi en masse depuis ce domaine. Il sert à répondre et à relancer des personnes qui nous ont déjà écrit, qui ont déjà reçu un mail de nous ou à qui on a parlé au téléphone. Le serveur refuse tout autre destinataire et plafonne à 30 mails par 24 h.
2. Par défaut, prépare un BROUILLON et montre-le-moi. N'envoie que si je te le demande clairement pour ce mail-là.
3. Écris en français, au vouvoiement, sans tiret long, sans promesse de résultat. Pas de prix précis : « à partir de 690 € » au plus, le prix se fixe à l'appel.
4. Après un appel ou un échange, ajoute une note sur le contact (contact_notes) et, s'il faut rappeler, une tâche (tasks).
5. Ne supprime rien sans me demander.`;
}

export function CleApi() {
  const moi = useMoi();
  const etat = useCharger<{ existe: boolean; debut: string | null; le: string | null }>("/api/cle");
  const boite = useCharger<{ boite: string }>("/api/crm/boite/moi");
  const [cle, setCle] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [copie, setCopie] = useState(false);
  const nouvelle = async () => {
    if (etat.data?.existe && !window.confirm("Créer une nouvelle clé ? L'ancienne cessera de fonctionner tout de suite.")) return;
    const d = await api.post<{ cle: string }>("/api/cle");
    setCle(d.cle); setVisible(true); etat.recharger();
  };
  const texte = cle ? consigne(cle, boite.data?.boite || "") : "";
  return (
    <Card>
      <CardHeader><CardTitle>Piloter le CRM depuis Claude Code</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          {moi?.nom ? `${moi.nom.split(" ")[0]}, si` : "Si"} vous voulez gérer ce CRM depuis votre Claude Code, donnez-lui cette clé avec le texte d'explication ci-dessous :
          copiez-le et collez-le tel quel dans Claude Code. La clé donne accès au CRM et à votre boîte mail, rien d'autre.
        </p>
        {!cle && (
          <p className="text-muted-foreground">
            {etat.data?.existe
              ? `Une clé existe déjà (${etat.data.debut}…, créée le ${new Date(etat.data.le || "").toLocaleDateString("fr-FR")}). Pour des raisons de sécurité, elle ne s'affiche qu'une fois : créez-en une nouvelle si vous l'avez perdue.`
              : "Vous n'avez pas encore de clé."}
          </p>
        )}
        {!cle ? (
          <Button onClick={nouvelle}>{etat.data?.existe ? "Créer une nouvelle clé" : "Afficher ma clé"}</Button>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setVisible(!visible)}>{visible ? "Masquer" : "Afficher"}</Button>
              <Button onClick={() => navigator.clipboard.writeText(texte).then(() => setCopie(true))}>{copie ? "Copié" : "Copier la clé et le texte pour Claude Code"}</Button>
            </div>
            {visible ? (
              <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted p-3 text-xs">{texte}</pre>
            ) : (
              <p className="font-mono text-xs">{cle.slice(0, 8)}••••••••••••••••••••</p>
            )}
            <p className="text-xs text-muted-foreground">Cette clé ne s'affichera plus après avoir quitté la page : gardez le texte copié.</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
